/**
 * WakeTrailPass.ts — persistent foam-trail texture (RTT).
 *
 * A compute pass owning a single-channel ping-pong texture (r32float). Each
 * frame it decays the existing foam by a per-frame factor and stamps the
 * line segment [previous-stern -> current-stern] with a soft falloff. The
 * water shader samples this texture bilinearly as a foam mask, which gives a
 * naturally-shaped wake stripe that:
 *   - persists in world space (doesn't drift with the camera),
 *   - fades over time (decay timescale TAU),
 *   - paints continuously even at high speed (segment stamping, not points),
 *   - costs O(N²) per frame where N=resolution (compute), and O(1) per pixel
 *     in the water fragment shader (one bilinear sample).
 *
 * Anchored in world space near the player boat. It recentres only when the
 * boat approaches the edge of the texture, and clears on that jump so old
 * foam is never reinterpreted in a shifted coordinate frame.
 */

import wgsl from "../shaders/wakeTrail.wgsl?raw";
import { createUniformBuffer } from "../gpu/Buffers";
import type { Boat } from "../sim/Boat";
import {
  getBoatCollisionLayers,
  type BoatCollisionProfile,
} from "../sim/Collisions";
import type { FrameContext, RenderPass } from "../rendering/RenderPass";

// 16 floats: centerXZ(2), extent, res, decay, halfWidth, amp, stampOn,
// boatXZ(2), fwdXZ(2), speed, length, prevBoatXZ(2).
const STRUCT_FLOATS = 16;
const STRUCT_SIZE = STRUCT_FLOATS * 4;

function sampleCollisionHalfBeam(
  profile: BoatCollisionProfile,
  x: number,
): number {
  const outline = profile.outline;
  if (outline.length === 0) return profile.halfBeam;
  let bestDx = Infinity;
  let best = 0;
  for (const p of outline) {
    const dx = Math.abs(p.x - x);
    if (dx < bestDx) {
      bestDx = dx;
      best = Math.abs(p.z);
    } else if (dx === bestDx) {
      best = Math.max(best, Math.abs(p.z));
    }
  }
  return best;
}

function sternWaterlineHalfBeam(boat: Boat): number | null {
  const profile = getBoatCollisionLayers(boat)[0]?.profile;
  if (!profile) return null;
  const span = Math.max(profile.xMax - profile.xMin, 1e-4);
  let best = 0;
  for (let i = 0; i < 4; i++) {
    const x = profile.xMin + (span * 0.12 * i) / 3;
    best = Math.max(best, sampleCollisionHalfBeam(profile, x));
  }
  return best > 0.05 ? best : null;
}

export interface WakeTrailPassOptions {
  /** Texture resolution per side. Default 256. */
  resolution?: number;
  /** World-space size covered by the texture (m). Default 128 → 0.5 m / pixel. */
  extent?: number;
  /** Fallback half-width of the foam stripe (m). Real boats use stern markers. */
  halfWidth?: number;
  /** Foam decay timescale (s). exp(-t/tau). Default 6. */
  tau?: number;
  /** Peak stamp intensity. Default 1. */
  amp?: number;
  /** Minimum wake drive before any stamping happens. Speed and throttle both contribute. */
  minSpeed?: number;
}

export class WakeTrailPass implements RenderPass {
  readonly name = "WakeTrailPass";

  readonly resolution: number;
  readonly extent: number;
  readonly halfWidth: number;
  readonly tau: number;
  readonly amp: number;
  readonly minSpeed: number;

  private device: GPUDevice;
  private boats: Boat[];
  private pipeline: GPUComputePipeline;
  private bgl: GPUBindGroupLayout;
  private uniform: GPUBuffer;
  private texA: GPUTexture;
  private texB: GPUTexture;
  private viewA: GPUTextureView;
  private viewB: GPUTextureView;
  private bgAB: GPUBindGroup;
  private bgBA: GPUBindGroup;
  private currentIsB = true;
  /** World-XZ centre of the trail texture. Kept stable while the boat moves. */
  readonly centerXZ: [number, number] = [0, 0];
  private centerInitialized = false;
  private prevSternXZ: [number, number] | null = null;

  constructor(
    device: GPUDevice,
    boats: Boat[],
    opts: WakeTrailPassOptions = {},
  ) {
    this.device = device;
    this.boats = boats;
    this.resolution = opts.resolution ?? 256;
    this.extent = opts.extent ?? 128;
    this.halfWidth = opts.halfWidth ?? 0.5;
    this.tau = opts.tau ?? 1.0;
    this.amp = opts.amp ?? 0.58;
    this.minSpeed = opts.minSpeed ?? 0.25;

    const usage =
      GPUTextureUsage.STORAGE_BINDING |
      GPUTextureUsage.TEXTURE_BINDING |
      GPUTextureUsage.COPY_DST;
    this.texA = device.createTexture({
      label: "WakeTrailPass.A",
      size: { width: this.resolution, height: this.resolution },
      format: "r32float",
      usage,
    });
    this.texB = device.createTexture({
      label: "WakeTrailPass.B",
      size: { width: this.resolution, height: this.resolution },
      format: "r32float",
      usage,
    });
    this.viewA = this.texA.createView({ label: "WakeTrailPass.A.view" });
    this.viewB = this.texB.createView({ label: "WakeTrailPass.B.view" });

    // Zero-initialise both textures.
    const zeros = new Float32Array(this.resolution * this.resolution);
    for (const tex of [this.texA, this.texB]) {
      device.queue.writeTexture(
        { texture: tex },
        zeros.buffer,
        { bytesPerRow: this.resolution * 4, rowsPerImage: this.resolution },
        { width: this.resolution, height: this.resolution },
      );
    }

    this.uniform = createUniformBuffer(
      device,
      STRUCT_SIZE,
      "WakeTrailPass.params",
    );

    const module = device.createShaderModule({
      label: "wakeTrail.wgsl",
      code: wgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "WakeTrailPass.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: { access: "write-only", format: "r32float" },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "WakeTrailPass.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      compute: { module, entryPoint: "cs_main" },
    });
    this.bgAB = this.makeBindGroup(this.viewA, this.viewB, "AB");
    this.bgBA = this.makeBindGroup(this.viewB, this.viewA, "BA");
  }

  private makeBindGroup(
    inView: GPUTextureView,
    outView: GPUTextureView,
    label: string,
  ): GPUBindGroup {
    return this.device.createBindGroup({
      label: `WakeTrailPass.bg.${label}`,
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: inView },
        { binding: 2, resource: outView },
      ],
    });
  }

  /** Currently most-recently-written view (water shader binding). */
  get currentView(): GPUTextureView {
    return this.currentIsB ? this.viewB : this.viewA;
  }

  views(): { a: GPUTextureView; b: GPUTextureView } {
    return { a: this.viewA, b: this.viewB };
  }

  get currentIsViewB(): boolean {
    return this.currentIsB;
  }

  private sternWakeHalfWidth(boat: Boat): number {
    const sternProfile = sternWaterlineHalfBeam(boat);
    if (sternProfile !== null) {
      return Math.max(this.halfWidth, sternProfile * 0.95);
    }

    const fallback = Math.max(this.halfWidth, boat.params.beam * 0.34);
    const named = boat.params.namedAnchors;
    if (named) {
      const stern: Array<[number, number, number]> = [];
      const buoyancy: Array<[number, number, number]> = [];
      for (const [key, pos] of Object.entries(named)) {
        if (!/^buoyancy[._\-]/i.test(key)) continue;
        buoyancy.push(pos);
        if (/^buoyancy[._\-]stern([._\-]|$)/i.test(key)) {
          stern.push(pos);
        }
      }

      const sternMaxZ = Math.max(0, ...stern.map((p) => Math.abs(p[2])));
      if (sternMaxZ > 0.05) {
        return Math.max(fallback, sternMaxZ * 0.78);
      }

      const sternC = stern.find((p) => Math.abs(p[2]) <= 0.05);
      if (sternC) {
        const stationMaxZ = Math.max(
          0,
          ...buoyancy
            .filter((p) => Math.abs(p[0] - sternC[0]) < 0.85)
            .map((p) => Math.abs(p[2])),
        );
        if (stationMaxZ > 0.05) {
          return Math.max(fallback, stationMaxZ * 0.78);
        }
      }
    }

    const presetSamples = boat.params.anchors?.buoyancySamples;
    if (presetSamples?.length) {
      const sternX = Math.min(...presetSamples.map((p) => p[0]));
      const sternMaxZ = Math.max(
        0,
        ...presetSamples
          .filter((p) => Math.abs(p[0] - sternX) < boat.params.length * 0.18)
          .map((p) => Math.abs(p[2])),
      );
      if (sternMaxZ > 0.05) {
        return Math.max(fallback, sternMaxZ * 0.78);
      }
    }

    return fallback;
  }

  encode(encoder: GPUCommandEncoder, frame: FrameContext): void {
    // Keep the texture stable in world space. Re-anchoring every frame makes
    // the already-painted foam slide in texture space, which looks like the
    // wake is overtaking the boat.
    const player = this.boats[0];
    let reanchored = false;
    let boatX = 0;
    let boatZ = 0;
    let fwdX = 0;
    let fwdZ = 0;
    let speed = 0;
    let wakeDrive = 0;
    let stampOn = 0;
    let prevBoatX = 0;
    let prevBoatZ = 0;
    let halfWidth = this.halfWidth;
    if (player) {
      const cellSize = this.extent / this.resolution;
      const anchorX = Math.round(player.posX / cellSize) * cellSize;
      const anchorZ = Math.round(player.posZ / cellSize) * cellSize;
      const distFromCenter = Math.hypot(
        player.posX - this.centerXZ[0],
        player.posZ - this.centerXZ[1],
      );
      if (!this.centerInitialized || distFromCenter > this.extent * 0.36) {
        this.centerXZ[0] = anchorX;
        this.centerXZ[1] = anchorZ;
        this.centerInitialized = true;
        this.prevSternXZ = null;
        reanchored = true;
      }

      const ch = Math.cos(player.heading);
      const sh = Math.sin(player.heading);
      // Boat-forward in world XZ (heading=0 → -Z axis).
      fwdX = sh;
      fwdZ = -ch;
      const sternOff = player.params.length * 0.45;
      boatX = player.posX - fwdX * sternOff;
      boatZ = player.posZ - fwdZ * sternOff;
      speed = Math.hypot(player.surge, player.sway);
      const forwardThrottle = Math.max(player.throttle, 0);
      const reverseThrottle = Math.max(-player.throttle, 0);
      // The long trail follows actual hull motion, but the near-stern wash
      // should also appear under throttle before the boat has accelerated.
      // Ahead 19% maps to ~0.5 m/s worth of visual drive; reverse is slightly
      // weaker because reverse thrust/prop wash is less efficient in the sim.
      wakeDrive = Math.max(speed, forwardThrottle * 2.6, reverseThrottle * 2.0);
      halfWidth = this.sternWakeHalfWidth(player);
      if (this.prevSternXZ !== null) {
        prevBoatX = this.prevSternXZ[0];
        prevBoatZ = this.prevSternXZ[1];
        const stepLen = Math.hypot(boatX - prevBoatX, boatZ - prevBoatZ);
        stampOn =
          wakeDrive > this.minSpeed &&
          stepLen < 8 &&
          (stepLen > 0.005 || Math.abs(player.throttle) > 0.08)
            ? 1
            : 0;
      } else {
        prevBoatX = boatX;
        prevBoatZ = boatZ;
      }
      this.prevSternXZ = [boatX, boatZ];
    } else {
      this.prevSternXZ = null;
    }

    const dt = Math.min(frame.dt, 1 / 30);
    const decay = reanchored ? 0 : Math.exp(-dt / this.tau);
    // Keep the visible foam as a near-stern disturbance, not a long trail.
    const vLength = Math.min(2.8, 1.45 + speed * 0.3);

    const buf = new Float32Array(STRUCT_FLOATS);
    buf[0] = this.centerXZ[0];
    buf[1] = this.centerXZ[1];
    buf[2] = this.extent;
    buf[3] = this.resolution;
    buf[4] = decay;
    buf[5] = halfWidth;
    buf[6] = this.amp;
    buf[7] = stampOn;
    buf[8] = boatX;
    buf[9] = boatZ;
    buf[10] = fwdX;
    buf[11] = fwdZ;
    buf[12] = wakeDrive;
    buf[13] = vLength;
    buf[14] = prevBoatX;
    buf[15] = prevBoatZ;
    this.device.queue.writeBuffer(this.uniform, 0, buf);

    const pass = encoder.beginComputePass({ label: "WakeTrailPass" });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.currentIsB ? this.bgBA : this.bgAB);
    const wg = Math.ceil(this.resolution / 8);
    pass.dispatchWorkgroups(wg, wg, 1);
    pass.end();
    this.currentIsB = !this.currentIsB;
  }

  destroy(): void {
    this.texA.destroy();
    this.texB.destroy();
  }
}
